/**
 * @fileoverview Tutor Invite Signup
 * @description Form for teachers to complete signup via invitation link
 * Route: /hello-teacher/:token
 */

import { Button, Loader, PasswordInput, Space, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { showNotification } from '@mantine/notifications'
import { Heading } from 'components'
import axios from 'axios'
import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from 'context/userAuthContext'
import { validateTutorInvite, submitTutorInviteSignup } from '../service'
import type { TutorInviteValidation } from '../service/types'
import { Form } from '../styles'

/**
 * Invite Signup form component
 * Validates token on mount, then shows password form
 */
export const InviteSignup = () => {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const { login } = useAuth()

  const [validating, setValidating] = useState(true)
  const [validation, setValidation] = useState<TutorInviteValidation | null>(null)
  const [tokenError, setTokenError] = useState<string | null>(null)

  const [visible, { toggle }] = useDisclosure(false)
  const [confirmVisible, { toggle: toggleConfirm }] = useDisclosure(false)
  const [loading, setLoading] = useState(false)

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
      passwordConfirmation: '',
    },
    validateInputOnBlur: true,
    validate: {
      email: value => {
        if (!value) return t('teacher_signup.validation_email_required')
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return t('teacher_signup.validation_email_invalid')
        return null
      },
      password: value => {
        if (!value) return t('teacher_signup.validation_password_required')
        if (value.length < 6) return t('teacher_signup.validation_password_min')
        if (value.length > 128) return t('teacher_signup.validation_password_max')
        return null
      },
      passwordConfirmation: (value, values) => {
        if (!value) return t('tutor_invite.validation_confirm_password_required')
        if (value !== values.password) return t('tutor_invite.validation_passwords_mismatch')
        return null
      },
    },
  })

  /**
   * Validate the invitation token on mount
   */
  useEffect(() => {
    if (!token) {
      setTokenError(t('tutor_invite.invalid_token'))
      setValidating(false)
      return
    }

    let cancelled = false

    validateTutorInvite(token).then(result => {
      if (cancelled) return
      if (result.valid) {
        setValidation(result)
        if (result.email) {
          form.setFieldValue('email', result.email)
        }
      } else {
        setTokenError(result.error || t('tutor_invite.invalid_token'))
      }
    }).catch(() => {
      if (!cancelled) setTokenError(t('tutor_invite.invalid_token'))
    }).finally(() => {
      if (!cancelled) setValidating(false)
    })

    return () => { cancelled = true }
  }, [token]) // token is stable from useParams

  /**
   * Handle form submission
   */
  const handleSubmit = async (values: typeof form.values) => {
    if (!token) return

    setLoading(true)

    try {
      await submitTutorInviteSignup({
        invitationToken: token,
        email: values.email,
        password: values.password,
        passwordConfirmation: values.passwordConfirmation,
      })

      // Auto-login with the same credentials
      const loginResponse = await axios.post('/api/auth/sign_in', {
        email: values.email,
        password: values.password,
      })

      const userData = loginResponse.data?.data
      if (userData) {
        login({
          email: userData.email,
          firstName: userData.firstName || userData.first_name,
          lastName: userData.lastName || userData.last_name,
          id: userData.id,
          locale: userData.locale,
          selectedSchoolId: userData.selected_school_id,
          username: userData.username,
          uid: userData.uid,
        })

        Cookies.set('access_token', loginResponse.headers['access-token'])
        Cookies.set('client', loginResponse.headers.client)
        Cookies.set('uid', loginResponse.headers.uid)
        Cookies.set('token-type', loginResponse.headers['token-type'])
      }

      window.location.href = '/teacher/dashboard'
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : t('tutor_invite.signup_error')
      showNotification({
        color: 'red',
        title: t('tutor_invite.signup_error'),
        message: errorMessage,
      })
    } finally {
      setLoading(false)
    }
  }

  // Loading state while validating token
  if (validating) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem 0' }}>
        <Loader size='lg' />
        <Text color='dimmed' mt='md'>
          {t('tutor_invite.validating_token')}
        </Text>
      </div>
    )
  }

  // Invalid token state
  if (tokenError) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Heading size='xl' text={t('tutor_invite.invalid_token_title')} />
        <Space h='md' />
        <Text color='red' size='sm'>
          {tokenError}
        </Text>
        <Space h='lg' />
        <Button
          variant='outline'
          radius='xl'
          component={Link}
          to='/account/signin'
        >
          {t('teacher_signup.go_to_login')}
        </Button>
      </div>
    )
  }

  const hasEmail = Boolean(validation?.email)

  return (
    <>
      <div style={{ textAlign: 'center' }}>
        <Heading size='xl' text={t('teacher_signup.page_title')} />
      </div>
      <Text color='dimmed' align='center' mb='xs'>
        {t('teacher_signup.page_subtitle')}
      </Text>

      {validation?.tutor?.name && (
        <Text align='center' mb='lg' size='sm' color='dimmed'>
          {t('tutor_invite.welcome_message', {
            name: validation.tutor.name,
            school: validation.school?.name || '',
          })}
        </Text>
      )}

      <Form onSubmit={form.onSubmit(handleSubmit)}>
        <TextInput
          size='md'
          label={t('teacher_signup.email_label')}
          placeholder={t('teacher_signup.email_placeholder')}
          readOnly={hasEmail}
          styles={hasEmail ? {
            input: { backgroundColor: '#f1f3f5', cursor: 'default' },
          } : undefined}
          {...form.getInputProps('email')}
        />

        <PasswordInput
          mt='md'
          size='md'
          label={t('teacher_signup.password_label')}
          placeholder={t('teacher_signup.password_placeholder')}
          visible={visible}
          onVisibilityChange={toggle}
          {...form.getInputProps('password')}
        />

        <PasswordInput
          mt='md'
          size='md'
          label={t('tutor_invite.confirm_password_label')}
          placeholder={t('tutor_invite.confirm_password_placeholder')}
          visible={confirmVisible}
          onVisibilityChange={toggleConfirm}
          {...form.getInputProps('passwordConfirmation')}
        />

        <Space h='xl' />

        <Button
          fullWidth
          size='lg'
          type='submit'
          loading={loading}
          radius='xl'
        >
          {t('tutor_invite.complete_signup_button')}
        </Button>
      </Form>
    </>
  )
}
